import { cn } from "@/lib/utils";
import { countryFlagSrc, countryFlagEmoji } from "@/lib/country-flags";

/**
 * Standard flag size scale (square tile side, in px). This is the single source
 * of truth for flag sizing — prefer a token over a raw pixel value so flags stay
 * consistent across the app.
 *
 *   sm — dense rows (spread/total selectors)
 *   md — default; inline team labels, pick buttons, card headers
 *   lg — emphasis (e.g. popovers, summaries)
 *
 * The artwork is gold country-map silhouettes (each a unique outline), so every
 * flag is rendered inside an identical square tile with `object-contain` — that
 * way they occupy equal space and read as uniform badges despite the differing
 * map shapes.
 */
export const FLAG_SIZES = { sm: 24, md: 28, lg: 40 } as const;
export type FlagSize = keyof typeof FLAG_SIZES;

function resolveSize(size: FlagSize | number): number {
  return typeof size === "number" ? size : FLAG_SIZES[size];
}

interface CountryFlagProps {
  /** Team/country name as it appears in event data (e.g. "Argentina", "USA"). */
  name: string | null | undefined;
  /** Size token ('sm' | 'md' | 'lg') or a raw height in px. Default 'md'. */
  size?: FlagSize | number;
  /**
   * 'badge' (default) renders the gold silhouette tile; 'emoji' renders the
   * emoji flag (e.g. 🇨🇿), falling back to the badge if no emoji exists.
   */
  variant?: "badge" | "emoji";
  className?: string;
}

/**
 * Renders a small country flag for recognized country names, or nothing at all
 * when the name isn't a known country — so it's safe to drop next to any team
 * name regardless of sport.
 */
export function CountryFlag({
  name,
  size = "md",
  variant = "badge",
  className,
}: CountryFlagProps) {
  const src = countryFlagSrc(name);
  if (!src) return null;

  const px = resolveSize(size);

  if (variant === "emoji") {
    const emoji = countryFlagEmoji(name);
    if (emoji) {
      return (
        <span
          aria-hidden="true"
          style={{ fontSize: Math.round(px * 0.75), lineHeight: 1 }}
          className={cn("inline-block shrink-0 align-middle", className)}
        >
          {emoji}
        </span>
      );
    }
  }

  return (
    <span
      style={{ width: px, height: px }}
      className={cn(
        "inline-flex items-center justify-center shrink-0 align-middle rounded-[4px] bg-cream/6 ring-1 ring-cream/15 p-px",
        className,
      )}
    >
      <img
        src={src}
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="max-h-full max-w-full object-contain"
      />
    </span>
  );
}
